import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  setDoc,
  writeBatch,
  type Firestore,
} from "firebase/firestore";
import { getFirebaseFirestore } from "./firebase";
import { omitUndefinedDeep } from "./omitUndefined";
import type { SavedRoom } from "./types";

/**
 * Low-level Firestore CRUD for SavedRoom documents.
 *
 * Each document lives under `users/{uid}/rooms/{roomId}` where `roomId`
 * matches `SavedRoom.id` — so the client-generated id is the same on
 * every device, which keeps deduping trivial.
 *
 * All functions no-op (or return a no-op unsubscribe) when Firebase
 * isn't configured for this environment, so the rest of the app can
 * call them unconditionally without an `if (configured)` dance.
 */

/** Thrown when a cloud op is attempted but Firestore isn't available. */
export class CloudUnavailableError extends Error {
  constructor() {
    super("Firestore isn't configured for this environment.");
    this.name = "CloudUnavailableError";
  }
}

function userRooms(db: Firestore, uid: string) {
  return collection(db, "users", uid, "rooms");
}

/**
 * Upsert (create or replace) a single SavedRoom for the given user.
 * Resolves when the local write has been queued; Firestore handles the
 * eventual server-side persistence with built-in offline buffering.
 */
export async function upsertRoom(
  uid: string,
  room: SavedRoom
): Promise<void> {
  const db = getFirebaseFirestore();
  if (!db) throw new CloudUnavailableError();
  const ref = doc(db, "users", uid, "rooms", room.id);
  await setDoc(ref, toCloudShape(room));
}

/** Delete a single SavedRoom. No-ops if the doc doesn't exist. */
export async function deleteRoom(uid: string, roomId: string): Promise<void> {
  const db = getFirebaseFirestore();
  if (!db) throw new CloudUnavailableError();
  const ref = doc(db, "users", uid, "rooms", roomId);
  await deleteDoc(ref);
}

/**
 * Delete every SavedRoom for the user. Uses a batched write so the
 * deletes commit atomically (up to Firestore's 500-op batch limit;
 * we chunk above that).
 */
export async function clearAllRooms(uid: string): Promise<void> {
  const db = getFirebaseFirestore();
  if (!db) throw new CloudUnavailableError();
  const snap = await getDocs(userRooms(db, uid));
  if (snap.empty) return;
  const refs = snap.docs.map((d) => d.ref);
  const CHUNK = 450;
  for (let i = 0; i < refs.length; i += CHUNK) {
    const batch = writeBatch(db);
    for (const ref of refs.slice(i, i + CHUNK)) batch.delete(ref);
    await batch.commit();
  }
}

/**
 * One-shot fetch of every saved room for the user. Returns an empty
 * array if Firestore isn't configured (so callers fall back to the
 * local-only library transparently).
 */
export async function fetchAllRooms(uid: string): Promise<SavedRoom[]> {
  const db = getFirebaseFirestore();
  if (!db) return [];
  const snap = await getDocs(userRooms(db, uid));
  return snap.docs
    .map((d) => fromCloudShape(d.id, d.data() as Record<string, unknown>))
    .filter((r): r is SavedRoom => r !== null);
}

/**
 * Subscribe to realtime changes for the user's rooms collection. The
 * callback fires immediately with the current snapshot (from cache if
 * offline), and again on every server-side change.
 *
 * Returns a no-op unsubscribe when Firestore isn't configured.
 */
export function subscribeRooms(
  uid: string,
  cb: (rooms: SavedRoom[]) => void,
  onError?: (err: Error) => void
): () => void {
  const db = getFirebaseFirestore();
  if (!db) return () => {};
  const unsub = onSnapshot(
    userRooms(db, uid),
    (snap) => {
      const rooms = snap.docs
        .map((d) => fromCloudShape(d.id, d.data() as Record<string, unknown>))
        .filter((r): r is SavedRoom => r !== null);
      cb(rooms);
    },
    (err) => {
      onError?.(err as Error);
    }
  );
  return unsub;
}

/**
 * Convert a SavedRoom into the plain object we persist to Firestore.
 * We deep-clone so the cloud copy isn't tied to the in-memory object
 * graph (mutating the live store later won't accidentally re-emit a
 * write through structural sharing).
 */
function toCloudShape(room: SavedRoom): Record<string, unknown> {
  // Clone first so the stored copy isn't the live store object, then drop
  // `undefined` fields. `structuredClone` keeps them, and `setDoc` rejects them.
  return omitUndefinedDeep({
    id: room.id,
    name: room.name,
    savedAt: room.savedAt,
    updatedAt: room.updatedAt,
    design: structuredClone(room.design),
  });
}

/**
 * Validate-and-narrow a raw Firestore doc into a SavedRoom. Returns
 * `null` if any required field is missing or wrong-shaped — the doc
 * is then silently dropped rather than crashing the whole sync.
 */
function fromCloudShape(
  id: string,
  data: Record<string, unknown>
): SavedRoom | null {
  if (
    typeof data.name !== "string" ||
    typeof data.savedAt !== "number" ||
    typeof data.updatedAt !== "number" ||
    typeof data.design !== "object" ||
    data.design === null
  ) {
    return null;
  }
  return {
    id,
    name: data.name,
    savedAt: data.savedAt,
    updatedAt: data.updatedAt,
    // Trust the client schema — both writer and reader are this app.
    // We deep-clone so the consumer can mutate without echoing into
    // any cached Firestore snapshot.
    design: structuredClone(data.design) as SavedRoom["design"],
  };
}
